/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Row } from "react-bootstrap";
import { Button, Table, Alert, Input, Select, FieldGroup } from "core/components";
import {
  InventoryItemFormData,
  ProjectFormData,
  ProjectRequisitionFormData,
  ProjectRequisitionsSchema,
  ProjectStatus,
  TaskFormData
} from "core/schema";
import { api, ApiError } from "core/api";
import { requisitions_admin_project_path } from "routes";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { IMaskInput } from "react-imask";
import DatePicker from "react-datepicker";
import moment from "moment";
import ProjectRequisitionContext from "./ProjectRequisitionContext";

type ProjectRequisitionFormProps = {
  project: ProjectFormData;
  task: TaskFormData;
  inventory_items: Array<InventoryItemFormData>;
};

const SelectField = ({ setSkipPageReset, setValue, control, watch, props, project }) => {
  const { selectedItems, setSelectedItems, isItemsLoading, inventoryItems } = React.useContext(ProjectRequisitionContext);
  const fieldArrayName = "data";
  const index = props.row.index;
  const fieldItem = `${fieldArrayName}.${index}`;
  const requisitionItem = watch(fieldItem);
  const [selectedItem, setSelectedItem] = React.useState<InventoryItemFormData>(
    inventoryItems.find((inventory_item) => inventory_item.id == requisitionItem?.item_id)
  );

  const addToSelectedItems = (item) => {
    if (selectedItems.find((i) => i.id == item.id)) {
      return;
    } else {
      setSelectedItems((value) => [...value, item]);
    }
  };

  const removeFromSelectedItems = (item) => {
    if (selectedItems.find((selectedItem) => selectedItem.id == item?.id)) {
      setSelectedItems(selectedItems.filter((selectedItem) => selectedItem.id != item?.id));
    }
  };

  React.useEffect(() => {
    if (selectedItem) {
      addToSelectedItems(selectedItem);
    }
  }, [selectedItem]);

  return (
    <Controller
      name={`${fieldArrayName}.${index}.item_id`}
      control={control}
      render={({ field }) => (
        <Select
          {...field}
          placeholder="Select Item"
          isLoading={isItemsLoading}
          options={inventoryItems.filter(
            (inventoryItem) => !selectedItems.find((selected) => selected.id == inventoryItem.id && field?.value != inventoryItem.id) || null
          )}
          getOptionLabel={(option) => `${option.code} - ${option.name}`}
          getOptionValue={(option) => option.id}
          isOptionDisabled={(option) => !option.active}
          onChange={(option, actionMeta) => {
            field.onChange(option?.id);

            if (selectedItem) {
              removeFromSelectedItems(selectedItem);
            }

            setSelectedItem(option);

            if (actionMeta.action == "select-option") {
              setValue(`${fieldItem}.inventory_item`, option);
              setValue(`${fieldItem}.unit_measure_code`, option?.unit_measure?.code);
              setValue(`${fieldItem}.budgeted_quantity`, 0);
              setValue(`${fieldItem}.actual_quantity`, 0);
              setValue(`${fieldItem}.committed_quantity`, 0);
              setValue(`${fieldItem}.remaining_quantity`, 0);
              setValue(`${fieldItem}.inventory_quantity`, option?.inventory_quantity);
              addToSelectedItems(option);
            } else {
              setValue(`${fieldItem}.inventory_item`, null);
              setValue(`${fieldItem}.unit_measure_code`, null);
              setValue(`${fieldItem}.budgeted_quantity`, null);
              setValue(`${fieldItem}.actual_quantity`, null);
              setValue(`${fieldItem}.committed_quantity`, null);
              setValue(`${fieldItem}.remaining_quantity`, null);
              setValue(`${fieldItem}.inventory_quantity`, null);
              removeFromSelectedItems(option || actionMeta?.removedValues[0]);
            }
            setValue(`${fieldItem}.project_id`, project.id);
            setValue(`${fieldItem}.source`, "requisition");
            setValue(`${fieldItem}.project_task_item_id`, props.row.original.id);
          }}
          ref={field.ref}
          onBlur={() => setSkipPageReset(true)}
          value={inventoryItems.find((inventory_item) => inventory_item.id == field.value) || null}
          errorMessage={props.errors[fieldArrayName]?.[index]?.item_id?.message}
        />
      )}
    />
  );
};

const ProjectRequisitionForm = ({ project, task, inventory_items, isItemsLoading, onCancel, onSubmit }: ProjectRequisitionFormProps): JSX.Element => {
  const [requisitions, setRequisitions] = React.useState([]);
  const [inventoryItems, setInventoryItems] = React.useState(inventory_items);
  const [selectedItems, setSelectedItems] = React.useState<Array<InventoryItemFormData>>([]);
  const [skipPageReset, setSkipPageReset] = React.useState(false);
  const [skipRequisitionFetch, setSkipRequisitionFetch] = React.useState(false);
  const table = React.useRef(null);
  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    resetField,
    watch,
    getValues,
    formState: { isSubmitting, isValidating, errors }
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(ProjectRequisitionsSchema)
  });
  const fieldArrayName = "data";
  const { fields, append, remove } = useFieldArray<ProjectRequisitionFormData>({
    control,
    name: fieldArrayName,
    keyName: "key"
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);

  const getRequisitions = async () => {
    try {
      const response = await api.get(requisitions_admin_project_path({ id: project.id }));
      setRequisitions(response.data);
      setSelectedItems(response.data.map((requisition) => requisition.item));
      reset({ data: response.data });
    } catch (error) {
      console.error(error);
    }
  };

  const canAppendItem = () => {
    return fields.length < inventory_items.length;
  };

  const removeFromSelectedItems = (index: number) => {
    if (selectedItems.find((_selectedItem, i) => i == index)) {
      setSelectedItems(selectedItems.filter((_selectedItem, i) => i != index) || []);
    }
  };

  React.useEffect(() => {
    if ((isItemsLoading || !isItemsLoading) && !skipRequisitionFetch) {
      getRequisitions();
      setSkipRequisitionFetch(true);
    } else if (!isItemsLoading && inventoryItems.length == 0) {
      setInventoryItems(inventory_items);
      setSkipRequisitionFetch(true);
    }
  }, [isItemsLoading]);

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id",
        Cell: (props) => {
          return <></>;
        }
      },
      {
        id: 1,
        Header: "Task ID",
        accessor: "task.code"
      },
      {
        id: 2,
        Header: "Task Name",
        accessor: "task.name"
      },
      {
        id: 3,
        Header: "Item ID",
        accessor: "item.code"
      },
      {
        id: 4,
        Header: "Item",
        accessor: "item.name"
      },
      {
        Header: "Task",
        accessor: "task",
        Cell: (props) => {
          const task = props.value;

          return (
            <>
              <div className="text-bold text-truncate" title={task.code} data-bs-toggle="tooltip">
                {task.code}
              </div>
              <p className="text-truncate" title={task.name} data-bs-toggle="tooltip">
                {task.name}
              </p>
            </>
          );
        }
      },
      {
        Header: "Inventory ID",
        accessor: "item",
        style: {
          width: "20%"
        },
        Cell: (props) => {
          const item = props.value;

          if (item?.id) {
            const stockable = item.stockable;
            const label = stockable ? "STOCKABLE" : "NOT STOCKABLE";
            return (
              <>
                <span className="text-bold" title={item.code} data-bs-toggle="tooltip">
                  {item.code}{" "}
                  <span
                    className={`badge badge--pill badge--${stockable ? "success" : "draft"}`}
                    style={{ flex: 1, justifyContent: "flex-start", fontSize: "9px" }}
                    title={label}
                    data-bs-toggle="tooltip"
                  >
                    {label}
                  </span>
                </span>
                <p style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }} title={item.name} data-bs-toggle="tooltip">
                  {item.name}
                </p>
              </>
            );
          } else {
            return (
              <SelectField setSkipPageReset={setSkipPageReset} setValue={setValue} control={control} watch={watch} props={props} project={project} />
            );
          }
        }
      },
      {
        Header: "UOM",
        accessor: "unit_measure_code",
        Cell: (props) => {
          const index = props.row.index;
          return <p className="text-center">{props.value || getValues(`data.${index}.unit_measure_code`)}</p>;
        }
      },
      {
        Header: "Budgeted Quantity",
        accessor: "budgeted_quantity",
        Cell: (props) => {
          const index = props.row.index;
          if (props.row.values.id) {
            setValue(`data.${props.row.index}.project_id`, project.id);
            setValue(`data.${props.row.index}.source`, "requisition");
            setValue(`data.${props.row.index}.project_task_item_id`, props.row.original.id);
            setValue(`data.${props.row.index}.budgeted_quantity`, props.value);
          }

          return <p className="text-center">{props.value || getValues(`data.${index}.budgeted_quantity`)}</p>;
        }
      },
      {
        Header: "Actual Issued Quantity",
        accessor: "actual_quantity",
        Cell: (props) => {
          const index = props.row.index;
          return <p className="text-center">{props.value || getValues(`data.${index}.actual_quantity`)}</p>;
        }
      },
      {
        Header: "Open Request",
        accessor: "committed_quantity",
        Cell: (props) => {
          const index = props.row.index;
          return <p className="text-center">{props.value || getValues(`data.${index}.committed_quantity`)}</p>;
        }
      },
      {
        Header: "Remaining Quantity",
        accessor: "remaining_quantity",
        Cell: (props) => {
          const index = props.row.index;
          return <p className="text-center">{props.value || getValues(`data.${index}.remaining_quantity`)}</p>;
        }
      },
      {
        Header: "Current Inventory",
        accessor: "inventory_quantity",
        Cell: (props) => {
          const index = props.row.index;
          return (
            <div className="text-center text-truncate" title={props.value || getValues(`data.${index}.inventory_quantity`)} data-bs-toggle="tooltip">
              <p className="text-center">{props.value || getValues(`data.${index}.inventory_quantity`)}</p>
            </div>
          );
        }
      },
      {
        Header: "Request Quantity",
        accessor: "requested_quantity",
        Cell: (props) => {
          let canRequest = props.row.original.can_request;
          const isNewItem = props.row.original.task_id;
          const inventoryQuantity = props.row.original.inventory_quantity || getValues(`data.${props.row.index}.inventory_quantity`);

          if (isNewItem) {
            const stockable = getValues(`data.${props.row.index}.inventory_item`)?.stockable;
            canRequest = stockable ? getValues(`data.${props.row.index}.inventory_quantity`) > 0 : true;
          }

          return (
            <Controller
              name={`data.${props.row.index}.requested_quantity`}
              control={control}
              render={({ field, fieldState }) => (
                <FieldGroup errorMessage={props.errors.data?.[props.row.index]?.requested_quantity?.message}>
                  <IMaskInput
                    mask={Number}
                    padFractionalZeros={true}
                    thousandsSeparator=","
                    radix="."
                    value={field.value?.toString()}
                    unmask={true}
                    inputRef={field.ref}
                    onAccept={(value) => {
                      if (value && inventoryQuantity > 0) {
                        setValue(`data.${props.row.index}.date_needed`, moment().toDate());
                      } else if (value && inventoryQuantity < 1) {
                        setValue(`data.${props.row.index}.date_needed`, moment().add(7, "days").toDate());
                      } else {
                        setValue(`data.${props.row.index}.date_needed`, null);
                      }
                      field.onChange(value);
                    }}
                    className="field"
                    placeholder={canRequest ? "0.0" : "No stock"}
                    min={0}
                    max={9999999}
                    readOnly={!canRequest}
                    style={fieldState.error ? { borderColor: "red" } : {}}
                  />
                </FieldGroup>
              )}
            />
          );
        }
      },
      {
        Header: "Date Needed",
        accessor: "date_needed",
        Cell: (props) => {
          let canRequest = props.row.original.can_request;
          const isNewItem = props.row.original.task_id;

          if (isNewItem) {
            const stockable = getValues(`data.${props.row.index}.inventory_item`)?.stockable;
            canRequest = stockable ? getValues(`data.${props.row.index}.inventory_quantity`) > 0 : true;
          }

          return (
            <Controller
              name={`data.${props.row.index}.date_needed`}
              control={control}
              render={({ field, fieldState }) => (
                <div className={`${fieldState.error ? "field-group--invalid" : ""}`}>
                  <DatePicker
                    selected={field.value}
                    onChange={(date) => {
                      field.onChange(date);
                    }}
                    onBlur={(e) => {
                      field.onBlur(e);
                    }}
                    ref={field.ref}
                    className="field"
                    minDate={moment().toDate()}
                    placeholder={canRequest ? "Select Date" : "No stock"}
                    readOnly={!canRequest}
                  />
                </div>
              )}
            />
          );
        }
      },
      {
        Header: "Remarks",
        accessor: "remarks",
        Cell: (props) => {
          let canRequest = props.row.original.can_request;
          const isNewItem = props.row.original.task_id;

          if (isNewItem) {
            const stockable = getValues(`data.${props.row.index}.inventory_item`)?.stockable;
            canRequest = stockable ? getValues(`data.${props.row.index}.inventory_quantity`) > 0 : true;
          }

          return <Input {...register(`data.${props.row.index}.remarks`)} readOnly={!canRequest} />;
        }
      },
      {
        accessor: "actions",
        style: {
          width: "5%"
        },
        Cell: (props) => {
          const index = props.row.index;
          if (!props.row.values.id) {
            return (
              <Button
                className="button--danger"
                icon="minus"
                iconProps={{ className: "svg-icon h5" }}
                onClick={() => {
                  resetField(`${fieldArrayName}.${index}`, { defaultValue: {} });
                  removeFromSelectedItems(index);
                  remove(index);
                }}
              />
            );
          } else {
            return <></>;
          }
        }
      }
    ],
    []
  );

  const updateData = (rowIndex, columnId, value) => {
    setSkipPageReset(true);
    setRequisitions((old) =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...old[rowIndex],
            [columnId]: value
          };
        }
        return row;
      })
    );
  };

  const save = async (data) => {
    const requisitions = data.data.filter((datum) => datum.requested_quantity);

    try {
      if (requisitions.length > 0) {
        await api.post(requisitions_admin_project_path({ id: project.id }), {
          requisitions
        });
      }
      onSubmit();
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const onError = (data) => {
    console.error(data);
    if (data?.data) {
      setFormErrors(["There are errors on your form."]);
    }
  };

  const dataFilter = () => {
    return (
      <>
        <Col>
          <Input placeholder="Search for Task ID" onChange={(e) => table.current.setFilter(1, e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Task Name" onChange={(e) => table.current.setFilter(2, e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Item ID" onChange={(e) => table.current.setFilter(3, e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Item Name" onChange={(e) => table.current.setFilter(4, e.target.value)} />
        </Col>
      </>
    );
  };

  return (
    <React.StrictMode>
      <ProjectRequisitionContext.Provider value={{ selectedItems, setSelectedItems, isItemsLoading, inventoryItems }}>
        {requisitions.length > 0 ? (
          <>
            <Col>
              {formErrors && formErrors.length != 0 && (
                <Alert>
                  {formErrors.map((error, index) => {
                    return <div key={index}>{error}</div>;
                  })}
                </Alert>
              )}
            </Col>
            <Row className="mb-3" style={{ paddingLeft: 0, paddingRight: 0 }}>
              {dataFilter()}
              <Col className="d-flex justify-content-end align-items-baseline">
                <Button
                  className="button--default mr-3"
                  icon="times"
                  title="Cancel"
                  type="button"
                  onClick={() => {
                    onCancel();
                  }}
                />
                <Button
                  className="button--brand"
                  icon="save"
                  title="Submit Requisition"
                  type="button"
                  loading={isSubmitting || isValidating}
                  onClick={() => {
                    handleSubmit(async (data) => await save(data), onError)();
                  }}
                />
              </Col>
            </Row>

            <Table
              columns={columns}
              data={fields}
              updateData={updateData}
              control={control}
              register={register}
              skipPageReset={skipPageReset}
              errors={errors}
              hiddenColumns={[1, 2, 3, 4]}
              ref={table}
            />
            {canAppendItem() && (
              <Col className="d-flex justify-content-start">
                <Button
                  className="button--brand mt-4"
                  title="Add Item"
                  icon="plus"
                  type="button"
                  onClick={() => {
                    append({ task, task_id: task.id });
                  }}
                />
              </Col>
            )}
          </>
        ) : (
          <>
            <div className="d-flex justify-content-end align-items-baseline">
              <Button
                className="button--default"
                icon="times"
                title="Cancel"
                type="button"
                onClick={() => {
                  onCancel();
                }}
              />
              {(project.project_tasks.some((project_task) => project_task.code == "N/A") ||
                [ProjectStatus.Punchlisting, ProjectStatus.Completed].includes(project.status)) && (
                <Button
                  className="button--brand ml-3"
                  icon="save"
                  title="Submit Requisition"
                  type="button"
                  loading={isSubmitting || isValidating}
                  onClick={() => {
                    handleSubmit(async (data) => await save(data), onError)();
                  }}
                />
              )}
            </div>
            <div className="text-center mt-4 mb-4">No available items for requisition.</div>
            {(project.project_tasks.some((project_task) => project_task.code == "N/A") ||
              [ProjectStatus.Punchlisting, ProjectStatus.Completed].includes(project.status)) && (
              <>
                <Table
                  columns={columns}
                  data={fields}
                  updateData={updateData}
                  control={control}
                  register={register}
                  skipPageReset={skipPageReset}
                  errors={errors}
                  hiddenColumns={[1, 2, 3, 4]}
                  ref={table}
                />
                {canAppendItem() && (
                  <Col className="d-flex justify-content-start">
                    <Button
                      className="button--brand mt-4"
                      title="Add Item"
                      icon="plus"
                      type="button"
                      onClick={() => {
                        append({ task, task_id: task.id });
                      }}
                    />
                  </Col>
                )}
              </>
            )}
          </>
        )}
      </ProjectRequisitionContext.Provider>
    </React.StrictMode>
  );
};

export default ProjectRequisitionForm;
